/**
 * API Interceptors -- Token Refresh on 401.
 *
 * Monkey-patches the global fetch to intercept 401 responses:
 * 1. On 401, attempts token refresh via POST /auth/refresh
 * 2. On refresh success, retries original request with new token
 * 3. On refresh failure, forces logout (clears auth state, redirects)
 *
 * Uses a request queue to prevent multiple concurrent refresh calls.
 */

import { useAuthStore } from '@/stores/useAuthStore';

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: Error) => void;
}> = [];

function processQueue(error: Error | null, token: string | null) {
  for (const promise of failedQueue) {
    if (error) {
      promise.reject(error);
    } else if (token) {
      promise.resolve(token);
    }
  }
  failedQueue = [];
}

function getBaseUrl(): string {
  return (
    (typeof import.meta !== 'undefined' &&
      import.meta.env?.VITE_API_BASE_URL) ||
    '/api'
  );
}

/**
 * Set up the token refresh interceptor.
 *
 * Call once at app startup (e.g., in App.tsx).
 * Patches window.fetch to intercept 401 responses from the API.
 */
export function setupTokenRefreshInterceptor(): void {
  const originalFetch = window.fetch;

  window.fetch = async (
    input: RequestInfo | URL,
    init?: RequestInit,
  ): Promise<Response> => {
    const response = await originalFetch(input, init);

    // Only intercept API calls that return 401
    const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
    const baseUrl = getBaseUrl();

    if (
      response.status !== 401 ||
      !url.startsWith(baseUrl) ||
      url.includes('/auth/login') ||
      url.includes('/auth/refresh') ||
      url.includes('/auth/register') ||
      url.includes('/auth/2fa/verify')
    ) {
      return response;
    }

    // 401 on an API call -- attempt refresh
    const store = useAuthStore.getState();
    const refreshToken = store.refreshToken;

    if (!refreshToken) {
      store.clearAuth();
      return response;
    }

    if (isRefreshing) {
      // Another refresh is in progress -- queue this request
      return new Promise<Response>((resolve, reject) => {
        failedQueue.push({
          resolve: async (newToken: string) => {
            // Retry with new token
            const headers = new Headers(init?.headers);
            headers.set('Authorization', `Bearer ${newToken}`);
            try {
              const retried = await originalFetch(input, {
                ...init,
                headers,
              });
              resolve(retried);
            } catch (err) {
              reject(err);
            }
          },
          reject: (err: Error) => reject(err),
        });
      });
    }

    isRefreshing = true;

    try {
      const refreshResponse = await originalFetch(
        `${baseUrl}/v1/auth/refresh`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh_token: refreshToken }),
        },
      );

      if (!refreshResponse.ok) {
        throw new Error('Refresh failed');
      }

      // The refresh endpoint wraps tokens in a { data: {...} } envelope. This
      // interceptor calls originalFetch directly (bypassing the api-client
      // unwrap), so unwrap here. Tolerate both shapes to be safe. Previously it
      // read data.access_token off the envelope -> undefined -> setTokens(undefined)
      // -> next request sent "Bearer undefined" -> 401 -> clearAuth -> logout
      // ~15min after login despite the user being active.
      const json = (await refreshResponse.json()) as {
        data?: { access_token: string; refresh_token: string };
        access_token?: string;
        refresh_token?: string;
      };
      const tokens = json.data ?? json;

      if (!tokens.access_token || !tokens.refresh_token) {
        throw new Error('Refresh response missing tokens');
      }

      store.setTokens(tokens.access_token, tokens.refresh_token);
      processQueue(null, tokens.access_token);

      // Retry original request with new token
      const headers = new Headers(init?.headers);
      headers.set('Authorization', `Bearer ${tokens.access_token}`);
      return originalFetch(input, { ...init, headers });
    } catch (err) {
      const error =
        err instanceof Error ? err : new Error('Token refresh failed');
      processQueue(error, null);
      store.clearAuth();
      return response;
    } finally {
      isRefreshing = false;
    }
  };
}
